import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import { post, get, put } from "../../../services/apiService";

/** EP50 upsert: existing global client linked via MPL (contract §4). */
const EXISTING_CLIENT_LINKED_MESSAGE =
  "An existing customer with this email was linked to your merchant account.";

const UPSERT_EXISTING_THRESHOLD_MS = 2000;

/**
 * Detect MPL link of a pre-existing client_info row (no backend flag in ClientInfoOut).
 * - create: created_at noticeably older than updated_at (update path, not fresh insert).
 * - update: upsert resolved to a different client_info id (email matched global row).
 */
const isExistingClientLinked = (result, contextClientInfoId = null) => {
  if (!result) return false;

  if (
    contextClientInfoId != null &&
    result.id != null &&
    Number(result.id) !== Number(contextClientInfoId)
  ) {
    return true;
  }

  if (contextClientInfoId != null) {
    return false;
  }

  if (!result.created_at || !result.updated_at) {
    return false;
  }

  const created = new Date(result.created_at).getTime();
  const updated = new Date(result.updated_at).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) {
    return false;
  }

  return updated - created > UPSERT_EXISTING_THRESHOLD_MS;
};

const getBillingUpsertSuccessFeedback = (result, mode, contextClientInfoId = null) => {
  if (isExistingClientLinked(result, contextClientInfoId)) {
    return { message: EXISTING_CLIENT_LINKED_MESSAGE, severity: "info" };
  }

  return {
    message:
      mode === "create"
        ? "Billing info created successfully"
        : "Billing info updated successfully",
    severity: "success",
  };
};

const BILLING_INFO_CREATE = "billingInfoForm/create";
const BILLING_INFO_UPDATE = "billingInfoForm/update";
const BILLING_INFO_GET_BY_ID = "billingInfoForm/getById";

export const getBillingInfoById = createAsyncThunk(
  BILLING_INFO_GET_BY_ID,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { token, billingInfoId } = payload;
      const url = `/billing-info/${billingInfoId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const billingInfoData = await get(url, config);
      return {
        billingInfoData,
        billingInfoId: billingInfoData.id,
      };
    } catch (error) {
      console.error("Error getting billing info by id thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error retrieving billing info",
          severity: "error",
        })
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error retrieving billing info",
      });
    }
  }
);

export const createBillingInfo = createAsyncThunk(
  BILLING_INFO_CREATE,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { token, handleGoBack, ...billingInfoData } = payload;
      const url = "/billing-info";
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      const result = await post(url, billingInfoData, config);
      const { message, severity } = getBillingUpsertSuccessFeedback(
        result,
        "create"
      );
      dispatch(showSnackbar({ message, severity }));
      if (handleGoBack) {
        handleGoBack();
      }
      return result;
    } catch (error) {
      console.error("Error creating billing info thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error creating billing info",
          severity: "error",
        })
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error creating billing info",
      });
    }
  }
);

export const updateBillingInfo = createAsyncThunk(
  BILLING_INFO_UPDATE,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { token, handleGoBack, billingInfoId, ...billingInfoData } =
        payload;
      const url = `/billing-info/${billingInfoId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      const result = await put(url, billingInfoData, config);
      const { message, severity } = getBillingUpsertSuccessFeedback(
        result,
        "update",
        billingInfoId
      );
      dispatch(showSnackbar({ message, severity }));
      if (handleGoBack) {
        handleGoBack();
      }
      return { billingInfoData: result, billingInfoId };
    } catch (error) {
      console.error("Error updating billing info thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error updating billing info",
          severity: "error",
        })
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error updating billing info",
      });
    }
  }
);
